import type { TypeSelectOption } from '@thefarmersfront/kitchen';

import { EXTERNAL_URLS } from '~/constants/externalUrls';

/**
 * [배송대행 관리] 페이지네이션 관련 상수
 */
export const INITIAL_DELIVERY_AGENCY_PAGE_SIZE = 50;
export const INITIAL_DELIVERY_AGENCY_PAGE_CURSOR = 1;

export const DELIVERY_AGENCY_PAGE_SIZE = {
  50: {
    value: 50,
    text: '50개',
  },
  100: {
    value: 100,
    text: '100개',
  },
  200: {
    value: 200,
    text: '200개',
  },
} as const;

export type TypeClaimPageSize = keyof typeof DELIVERY_AGENCY_PAGE_SIZE;

/**
 * [배송대행 관리][운송장 관리] 페이지네이션 사이즈 옵션 (운송장 관리 전용)
 */
export const DELIVERY_AGENCY_TRACKING_PAGE_SIZE = {
  ...DELIVERY_AGENCY_PAGE_SIZE,
  300: {
    value: 300,
    text: '300개',
  },
  500: {
    value: 500,
    text: '500개',
  },
} as const;

/**
 * [배송대행 관리] 날짜 검색 최대 기간
 */
export const MAX_DELIVERY_AGENCY_DATE_RANGE = 31;

/**
 * [배송대행 관리] 상품명 검색 키워드 타입 코드
 * 상품명 키워드 선택 시 배송 예정일을 필수로 강제하기 위한 분기 기준값
 */
export const DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE = 'PRODUCT_NAME';

/**
 * [배송대행 관리] 주문 상태
 * TODO - 출고완료 추후에 추가 (오픈스펙 아님)
 */
export const DELIVERY_AGENCY_ORDER_STATUS = {
  OUTBOUND_READY: { value: 'OUTBOUND_READY', text: '출고대기' },
  OUTBOUND_PROCESSING: { value: 'OUTBOUND_PROCESSING', text: '출고 진행중' },
  ON_THE_WAY: { value: 'ON_THE_WAY', text: '배송중' },
  DELIVERY_COMPLETED: { value: 'DELIVERY_COMPLETED', text: '배송완료' },
  CANCELED: { value: 'CANCELED', text: '주문취소' },
  DELIVERY_FAILED: { value: 'DELIVERY_FAILED', text: '배송실패' },
} as const;

export type TypeDeliveryAgencyOrderStatus =
  (typeof DELIVERY_AGENCY_ORDER_STATUS)[keyof typeof DELIVERY_AGENCY_ORDER_STATUS]['value'];

/**
 * [배송대행 관리] 주문 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_ORDER_STATUS.OUTBOUND_READY.value]: 'secondary',
  [DELIVERY_AGENCY_ORDER_STATUS.OUTBOUND_PROCESSING.value]: 'info',
  [DELIVERY_AGENCY_ORDER_STATUS.ON_THE_WAY.value]: 'info',
  [DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_COMPLETED.value]: 'success',
  [DELIVERY_AGENCY_ORDER_STATUS.CANCELED.value]: 'error',
  [DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_FAILED.value]: 'warning',
} as const;

/**
 * [배송대행 관리] 운송장 상태
 */
export const DELIVERY_AGENCY_SHIPPING_LABEL_STATUS = {
  OUTBOUND_PROCESSING: { value: 'OUTBOUND_PROCESSING', text: '출고 진행중' },
  ON_THE_WAY: { value: 'ON_THE_WAY', text: '배송중' },
  DELIVERY_COMPLETED: { value: 'DELIVERY_COMPLETED', text: '배송완료' },
  DELIVERY_CANCELED: { value: 'DELIVERY_CANCELED', text: '배송취소' },
  DELIVERY_FAILED: { value: 'DELIVERY_FAILED', text: '배송실패' },
} as const;

export type TypeDeliveryAgencyShippingLabelStatus =
  (typeof DELIVERY_AGENCY_SHIPPING_LABEL_STATUS)[keyof typeof DELIVERY_AGENCY_SHIPPING_LABEL_STATUS]['value'];

/**
 * [배송대행 관리] 운송장 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.OUTBOUND_PROCESSING.value]: 'info',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.ON_THE_WAY.value]: 'info',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED.value]: 'success',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED.value]: 'error',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_FAILED.value]: 'warning',
} as const;

/**
 * [배송대행관리] 배송사 코드 매핑
 */
export const DELIVERY_AGENCY_COURIER_CODE = {
  CJT: {
    value: 'CJT',
    text: 'CJ',
  },
  NM: {
    value: 'NM',
    text: '넥스트마일',
  },
  LTT: {
    value: 'LTT',
    text: '롯데',
  },
} as const;

export const DELIVERY_AGENCY_TRACKING_LINKS = {
  [DELIVERY_AGENCY_COURIER_CODE.CJT.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_CJT,
  [DELIVERY_AGENCY_COURIER_CODE.NM.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_KURLY_NEXT_MILE,
  [DELIVERY_AGENCY_COURIER_CODE.LTT.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_LTT,
} as const;

export type TypeDeliveryAgencyCourierCode = keyof typeof DELIVERY_AGENCY_COURIER_CODE;

/**
 * [배송대행관리][운송장관리] 운송장 출력
 */
export const DELIVERY_AGENCY_TRACKING_PRINT_STATUS = {
  Y: { value: 'Y', text: '출력함' },
  N: { value: 'N', text: '출력안함' },
} as const;

export type TypeDeliveryAgencyTrackingPrintStatus =
  (typeof DELIVERY_AGENCY_TRACKING_PRINT_STATUS)[keyof typeof DELIVERY_AGENCY_TRACKING_PRINT_STATUS]['value'];

/**
 * [배송 결과 리포트] 배송 유형 코드
 */
export const DELIVERY_AGENCY_DELIVERY_TYPE = {
  DAWN: 'DAWN',
  DAY: 'DAY',
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 운송장 상태
 */
export const DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS = {
  ON_THE_WAY: { value: 'ON_THE_WAY', text: '배송중' },
  DELIVERY_COMPLETED: { value: 'DELIVERY_COMPLETED', text: '배송완료' },
  DELIVERY_CANCELED: { value: 'DELIVERY_CANCELED', text: '배송취소' },
  DELIVERY_FAILED: { value: 'DELIVERY_FAILED', text: '배송실패' },
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 운송장 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.ON_THE_WAY.value]: 'info',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED.value]: 'success',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_FAILED.value]: 'warning',
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 배송 결과
 */
export const DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT = {
  NORMAL: { value: 'NORMAL', text: '정상배송' },
  DELAYED: { value: 'DELAYED', text: '지연배송' },
  NON_DELIVERED: { value: 'NON_DELIVERED', text: '미배송' },
  ALTERNATIVE_DELIVERY: { value: 'ALTERNATIVE_DELIVERY', text: '대응배송' },
  CONVERTED_DELIVERY: { value: 'CONVERTED_DELIVERY', text: '전환배송' },
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 배송 결과 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR = {
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.NORMAL.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.ALTERNATIVE_DELIVERY.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.DELAYED.value]: 'warning',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.CONVERTED_DELIVERY.value]: 'warning',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.NON_DELIVERED.value]: 'error',
} as const;

export const DELIVERY_AGENCY_SORT_BY = {
  CREATED_AT_DESC: 'CREATED_AT_DESC',
  CREATED_AT_ASC: 'CREATED_AT_ASC',
} as const;

export type TypeDeliveryAgencySortBy = (typeof DELIVERY_AGENCY_SORT_BY)[keyof typeof DELIVERY_AGENCY_SORT_BY];

export const DELIVERY_AGENCY_ORDER_SORT_OPTIONS: TypeSelectOption<TypeDeliveryAgencySortBy>[] = [
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC, text: '등록일 (내림차순)' },
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_ASC, text: '등록일 (오름차순)' },
];

export const DELIVERY_AGENCY_CREATED_SORT_OPTIONS: TypeSelectOption<TypeDeliveryAgencySortBy>[] = [
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC, text: '생성일 (내림차순)' },
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_ASC, text: '생성일 (오름차순)' },
];
